import { SpaceScope } from "../passapp-sdk/types/spaceTypes";

/** Campos donde puede venir el nombre del espacio. */
const NAME_FIELDS = ["name", "spaceName", "title"];

/** Campos de una etiqueta suelta, en el orden en que se muestran detrás del nombre.
 * `refOnly` marca los que, cuando vienen como string, traen el id y no el nombre:
 * de esos solo sirve la versión expandida. */
const LABEL_FIELDS: Array<{ field: string; refOnly?: boolean }> = [
  { field: "spaceGroupName" },
  { field: "groupName" },
  { field: "group", refOnly: true },
  { field: "spaceGroup", refOnly: true },
  { field: "buildingName" },
  { field: "building", refOnly: true },
  { field: "location" },
  { field: "sector" },
  { field: "floor" },
];

const asString = (value: any): string => (typeof value === "string" ? value.trim() : "");

/** Un id puede viajar suelto o dentro del objeto que describe al espacio. */
const idOf = (value: any): string => {
  if (typeof value === "string" || typeof value === "number") return String(value).trim();
  if (value && typeof value === "object") {
    return asString(value.id) || asString(value._id) || asString(value.key);
  }
  return "";
};

/** Nombre legible de una etiqueta: puede ser el string o el objeto que la contiene. */
const nameOf = (value: any): string => {
  if (typeof value === "string") return value.trim();
  if (value && typeof value === "object") {
    for (const field of NAME_FIELDS) {
      const name = asString(value[field]);
      if (name) return name;
    }
  }
  return "";
};

/** El login trae `space` con el id del espacio a cargo, si lo tiene. */
export const resolveSpaceId = (space: any): string => idOf(space);

/** Ubica el espacio a cargo dentro de lo que devolvió `globalOptions`. */
const findSpaceDetail = (data: any, spaceId: string): any => {
  if (!data) return null;

  // `space` puede venir ya resuelto (objeto) o seguir siendo el id.
  if (data.space && typeof data.space === "object") {
    const detailId = idOf(data.space);
    if (!detailId || detailId === spaceId) return data.space;
  }

  const lists = [data.spaces, data.spacesInCharge, data.securitySpaces];
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    const found = list.find((item: any) => idOf(item) === spaceId);
    if (found) return found;
  }

  return null;
};

/** Resuelve los ids de edificio del espacio contra el listado del usuario. */
const buildingLabels = (detail: any, buildings: any): string[] => {
  const ids = Array.isArray(detail?.buildings) ? detail.buildings : [];
  if (!ids.length || !Array.isArray(buildings)) return [];

  return ids
    .map((id: any) => {
      const buildingId = idOf(id);
      // El id puede venir ya expandido en el propio espacio.
      const direct = nameOf(id);
      if (direct && direct !== buildingId) return direct;
      const match = buildings.find((building: any) => idOf(building) === buildingId);
      return nameOf(match);
    })
    .filter(Boolean);
};

/** Etiquetas del espacio: lo que viene listo primero, después los campos sueltos. */
const spaceLabels = (detail: any, buildings: any): string[] => {
  const labels: string[] = [];

  const ready = Array.isArray(detail?.labels)
    ? detail.labels
    : Array.isArray(detail?.tags)
    ? detail.tags
    : [];
  ready.forEach((label: any) => labels.push(nameOf(label)));

  LABEL_FIELDS.forEach(({ field, refOnly }) => {
    const value = detail?.[field];
    // Un campo referencial que llega como string trae el id, no una etiqueta.
    if (refOnly && typeof value !== "object") return;
    labels.push(nameOf(value));
  });

  labels.push(...buildingLabels(detail, buildings));

  return labels.filter((label, index) => label && labels.indexOf(label) === index);
};

/**
 * Espacio a cargo del operador, listo para mostrar. El id ya lo trajo el login;
 * acá se le suma el detalle que arma `globalOptions`. Devuelve `null` cuando el
 * usuario no tiene espacio: es lo que distingue al login de reservas del resto.
 */
export const resolveSpaceScope = (
  spaceId: string,
  globalOptionsData?: any,
  buildings?: any
): SpaceScope | null => {
  if (!spaceId) return null;

  const detail = findSpaceDetail(globalOptionsData, spaceId);

  return {
    id: spaceId,
    name: nameOf(detail),
    labels: spaceLabels(detail, buildings),
  };
};

/** Texto del espacio para el encabezado: `Comedor · Piso 1`. */
export const spaceScopeTitle = (space?: SpaceScope | null): string =>
  space ? [space.name, ...space.labels].filter(Boolean).join(" · ") : "";
